import type { ReactNode } from "react";
const EMAIL_MAX_LENGTH = 320;
const URL_MAX_LENGTH = 2048;
const ASYNC_VALIDATOR_ARITY = 3;

/*
 * Validierungsregeln der Formularfassade (Plan 2026-10-13-antd-ausbau, Stufe 7) mit derselben Bedeutung wie bei
 * Antd (`async-validator`): `required`, `whitespace`, `type`, `min`/`max`/`len`, `pattern`, `enum`, `transform`,
 * `validator` (Versprechen oder Rückruf), `warningOnly`, Regeln als Funktion der Formularinstanz. Leere Werte
 * prüft nur `required`; die übrigen Regeln greifen erst bei einem Wert. Meldungen wie die bisherigen Antd-Texte.
 */

export type RuleType = "string" | "number" | "boolean" | "method" | "regexp" | "integer" | "float" | "object" | "enum" | "date" | "url" | "hex" | "email" | "array" | "tel";

/* Wie Antd: Werte und Regel-Callbacks sind untypisiert. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type RuleObject = {
  required?: boolean;
  message?: ReactNode;
  type?: RuleType;
  whitespace?: boolean;
  min?: number;
  max?: number;
  len?: number;
  pattern?: RegExp | string;
  enum?: unknown[];
  transform?: (value: any) => any;
  validator?: (rule: RuleObject, value: any, callback?: (error?: string) => void) => Promise<void | any> | void;
  validateTrigger?: string | string[];
  warningOnly?: boolean;
  defaultField?: RuleObject;
};
export type Rule = RuleObject | ((form: any) => RuleObject);
/* eslint-enable @typescript-eslint/no-explicit-any */

type Templates = {
  default: string;
  required: string;
  enum: string;
  whitespace: string;
  type: string;
  string: { len: string; min: string; max: string; range: string };
  number: { len: string; min: string; max: string; range: string };
  array: { len: string; min: string; max: string; range: string };
  mismatch: string;
};

const MESSAGES: Record<string, Templates> = {
  de: {
    default: "Validierungsfehler für ${label}",
    required: "Bitte geben Sie ${label} an",
    enum: "${label} muss eines der folgenden sein [${enum}]",
    whitespace: "${label} darf kein Leerzeichen sein",
    type: "${label} ist nicht gültig. ${type} erwartet",
    string: { len: "${label} muss genau ${len} Zeichen lang sein", min: "${label} muss mindestens ${min} Zeichen lang sein", max: "${label} darf höchstens ${max} Zeichen lang sein", range: "${label} muss zwischen ${min} und ${max} Zeichen lang sein" },
    number: { len: "${label} muss gleich ${len} sein", min: "${label} muss mindestens ${min} sein", max: "${label} darf maximal ${max} sein", range: "${label} muss zwischen ${min} und ${max} liegen" },
    array: { len: "Es müssen ${len} ${label} sein", min: "Es müssen mindestens ${min} ${label} sein", max: "Es dürfen maximal ${max} ${label} sein", range: "Die Anzahl an ${label} muss zwischen ${min} und ${max} liegen" },
    mismatch: "${label} entspricht nicht dem ${pattern} Muster",
  },
  en: {
    default: "Field validation error for ${label}",
    required: "Please enter ${label}",
    enum: "${label} must be one of [${enum}]",
    whitespace: "${label} cannot be a blank character",
    type: "${label} is not a valid ${type}",
    string: { len: "${label} must be ${len} characters", min: "${label} must be at least ${min} characters", max: "${label} must be up to ${max} characters", range: "${label} must be between ${min}-${max} characters" },
    number: { len: "${label} must be equal to ${len}", min: "${label} must be minimum ${min}", max: "${label} must be maximum ${max}", range: "${label} must be between ${min}-${max}" },
    array: { len: "Must be ${len} ${label}", min: "At least ${min} ${label}", max: "At most ${max} ${label}", range: "The amount of ${label} must be between ${min}-${max}" },
    mismatch: "${label} does not match the pattern ${pattern}",
  },
  ru: {
    default: "Ошибка проверки поля ${label}",
    required: "Пожалуйста, введите ${label}",
    enum: "${label} должен быть одним из [${enum}]",
    whitespace: "${label} не может быть пустым",
    type: "${label} не является типом ${type}",
    string: { len: "${label} должна быть ${len} символов", min: "${label} должна быть больше или равна ${min} символов", max: "${label} должна быть меньше или равна ${max} символов", range: "Длина ${label} должна быть между ${min}-${max} символами" },
    number: { len: "${label} должна быть равна ${len}", min: "${label} должна быть больше или равна ${min}", max: "${label} должна быть меньше или равна ${max}", range: "${label} должна быть между ${min}-${max}" },
    array: { len: "Количество элементов ${label} должно быть равно ${len}", min: "Количество элементов ${label} должно быть больше или равно ${min}", max: "Количество элементов ${label} должно быть меньше или равно ${max}", range: "Количество элементов ${label} должно быть между ${min} и ${max}" },
    mismatch: "${label} не соответствует шаблону ${pattern}",
  },
  uk: {
    default: "Помилка валідації для поля ${label}",
    required: "Будь ласка, заповніть ${label}",
    enum: "Лише одне зі значень [${enum}] доступне для ${label}",
    whitespace: "Значення у полі ${label} не може бути пробілом",
    type: "${label} не є типом ${type}",
    string: { len: "${label} має містити ${len} символів", min: "${label} має містити не менш, ніж ${min} символів", max: "${label} має містити не більш, ніж ${max} символів", range: "${label} має містити ${min}-${max} символів" },
    number: { len: "${label} має дорівнювати ${len}", min: "${label} має бути не менш, ніж ${min}", max: "${label} має бути не більш, ніж ${max}", range: "${label} має бути в межах ${min}-${max}" },
    array: { len: "${label} має містити ${len} елементи", min: "${label} має містити не менш, ніж ${min} елементи", max: "${label} має містити не більш, ніж ${max} елементи", range: "Кількість елементів в ${label} має бути в межах ${min}-${max}" },
    mismatch: "${label} не відповідає шаблону ${pattern}",
  },
  it: {
    default: "Errore di convalida del campo ${label}",
    required: "Si prega di inserire ${label}",
    enum: "${label} deve essere uno di [${enum}]",
    whitespace: "${label} non può essere un carattere vuoto",
    type: " ${label} non è un ${type} valido",
    string: { len: "${label} deve avere ${len} caratteri", min: "${label} deve contenere almeno ${min} caratteri", max: "${label} deve contenere fino a ${max} caratteri", range: "${label} deve contenere tra ${min}-${max} caratteri" },
    number: { len: "${label} deve essere uguale a ${len}", min: "${label} valore minimo è ${min}", max: "${label} valor e massimo è ${max}", range: "${label} deve essere compreso tra ${min}-${max}" },
    array: { len: "Deve essere ${len} ${label}", min: "Almeno ${min} ${label}", max: "Massimo ${max} ${label}", range: "Il totale di ${label} deve essere compreso tra ${min}-${max}" },
    mismatch: "${label} non corrisponde al modello ${pattern}",
  },
};

export function fillTemplate(template: string, variables: Record<string, unknown>): string {
  return template.replace(/\$\{(\w+)\}/g, (_, key: string) => (variables[key] === undefined ? `\${${key}}` : String(variables[key])));
}

const isEmpty = (value: unknown) => value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

const EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9 -퟿豈-﷏ﷰ-￯]+\.)+[a-zA-Z -퟿豈-﷏ﷰ-￯]{2,}))$/;
const URL_PATTERN = /^(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:localhost|(?:(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)(?:\.(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)*(?:\.(?:[a-z¡-￿]{2,})))(?::\d{2,5})?(?:[/?#][^\s]*)?$/i;

function typeMatches(type: RuleType, value: unknown): boolean {
  switch (type) {
    case "string": return typeof value === "string";
    case "number": return typeof value === "number" && !Number.isNaN(value);
    case "integer": return typeof value === "number" && Number.isInteger(value);
    case "float": return typeof value === "number" && !Number.isInteger(value);
    case "boolean": return typeof value === "boolean";
    case "array": return Array.isArray(value);
    case "object": return typeof value === "object" && value !== null && !Array.isArray(value);
    case "method": return typeof value === "function";
    case "regexp": return value instanceof RegExp;
    case "date": return value instanceof Date || (typeof value === "object" && value !== null && typeof (value as { isValid?: unknown }).isValid === "function");
    case "email": return typeof value === "string" && value.length <= EMAIL_MAX_LENGTH && EMAIL.test(value);
    case "url": return typeof value === "string" && value.length <= URL_MAX_LENGTH && URL_PATTERN.test(value);
    case "hex": return typeof value === "string" && /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i.test(value);
    case "tel": return typeof value === "string";
    default: return true;
  }
}

export type RuleResult = { errors: ReactNode[]; warnings: ReactNode[] };

function messageOf(rule: RuleObject, fallback: string, variables: Record<string, unknown>): ReactNode {
  if (rule.message === undefined || rule.message === null) return fillTemplate(fallback, variables);
  return typeof rule.message === "string" ? fillTemplate(rule.message, variables) : rule.message;
}

async function checkRule(rule: RuleObject, raw: unknown, templates: Templates, variables: Record<string, unknown>): Promise<ReactNode | null> {
  const value = rule.transform ? rule.transform(raw) : raw;
  const vars = { ...variables, ...("min" in rule ? { min: rule.min } : {}), ...("max" in rule ? { max: rule.max } : {}), ...("len" in rule ? { len: rule.len } : {}), type: rule.type, pattern: rule.pattern, enum: rule.enum?.join(", ") };
  if (rule.required && (isEmpty(value) || (rule.whitespace && typeof value === "string" && !value.trim()))) {
    return messageOf(rule, rule.whitespace && typeof value === "string" && value.length ? templates.whitespace : templates.required, vars);
  }
  if (rule.validator) {
    try {
      if (rule.validator.length >= ASYNC_VALIDATOR_ARITY) {
        const error = await new Promise<string | undefined>((resolve) => { const result = rule.validator!(rule, value, (message) => resolve(message)); if (result && typeof (result as Promise<unknown>).then === "function") (result as Promise<unknown>).then(() => resolve(undefined), (e) => resolve(e instanceof Error ? e.message : String(e))); });
        if (error) return rule.message !== undefined ? messageOf(rule, templates.default, vars) : error;
      } else {
        await rule.validator(rule, value);
      }
    } catch (error) {
      if (rule.message !== undefined) return messageOf(rule, templates.default, vars);
      if (error instanceof Error) return error.message;
      if (typeof error === "string") return error;
      return fillTemplate(templates.default, vars);
    }
    return null;
  }
  if (isEmpty(value)) return null;
  if (rule.whitespace && typeof value === "string" && !value.trim()) return messageOf(rule, templates.whitespace, vars);
  if (rule.type && rule.type !== "enum" && !typeMatches(rule.type, value)) return messageOf(rule, templates.type, vars);
  if (rule.enum && !rule.enum.includes(value)) return messageOf(rule, templates.enum, vars);
  if (rule.pattern !== undefined && typeof value === "string") {
    const pattern = typeof rule.pattern === "string" ? new RegExp(`^${rule.pattern}$`) : rule.pattern;
    pattern.lastIndex = 0;
    if (!pattern.test(value)) return messageOf(rule, templates.mismatch, vars);
  }
  const hasRange = rule.min !== undefined || rule.max !== undefined || rule.len !== undefined;
  if (hasRange) {
    const kind = typeof value === "number" ? "number" : Array.isArray(value) ? "array" : "string";
    const size = typeof value === "number" ? value : Array.isArray(value) ? value.length : typeof value === "string" ? Array.from(value).length : undefined;
    if (size !== undefined) {
      const set = templates[kind];
      if (rule.len !== undefined && size !== rule.len) return messageOf(rule, set.len, vars);
      if (rule.min !== undefined && rule.max !== undefined && (size < rule.min || size > rule.max)) return messageOf(rule, set.range, vars);
      if (rule.min !== undefined && rule.max === undefined && size < rule.min) return messageOf(rule, set.min, vars);
      if (rule.max !== undefined && rule.min === undefined && size > rule.max) return messageOf(rule, set.max, vars);
    }
  }
  return null;
}

/** Prüft alle Regeln eines Feldes; Fehler und Warnungen (`warningOnly`) getrennt. */
export async function runRules(rules: RuleObject[], value: unknown, label: string, lang: string): Promise<RuleResult> {
  const templates = MESSAGES[lang] ?? MESSAGES.de;
  const results = await Promise.all(rules.map(async (rule) => ({ rule, message: await checkRule(rule, value, templates, { label, name: label }) })));
  const errors: ReactNode[] = [];
  const warnings: ReactNode[] = [];
  for (const { rule, message } of results) {
    if (message === null || message === undefined) continue;
    (rule.warningOnly ? warnings : errors).push(message);
  }
  return { errors, warnings };
}
